import type { PromptContextChip } from "@/features/chat/components/PromptInput";

/** A cell attached to the next chat message. */
export interface ChatCell {
  rowKey: string;
  rowLabel: string;
  columnKey: string;
  columnLabel: string;
  /** The shown value; empty for an empty cell. */
  value: string;
  /** The words of the source the value came from. */
  quote: string;
}

export const chatCellKey = (rowKey: string, columnKey: string) => `${rowKey}\u0000${columnKey}`;

const short = (text: string, n = 48) => (text.length > n ? `${text.slice(0, n - 1).trimEnd()}…` : text);

/** Cells added to the chat after the ones already there, each once. */
export const withCells = (current: ChatCell[], added: ChatCell[]) => {
  const seen = new Set(current.map((c) => chatCellKey(c.rowKey, c.columnKey)));
  return [...current, ...added.filter((c) => !seen.has(chatCellKey(c.rowKey, c.columnKey)))];
};

/** One chip per cell, or per column when a column has several: "Dose · Wilding 2021", "Dose · 4 rows". */
export const cellChips = (cells: ChatCell[]): (PromptContextChip & { keys: string[] })[] => {
  const byColumn = new Map<string, ChatCell[]>();
  for (const cell of cells) byColumn.set(cell.columnKey, [...(byColumn.get(cell.columnKey) ?? []), cell]);
  return [...byColumn.values()].map((group) => ({
    key: group.map((c) => chatCellKey(c.rowKey, c.columnKey)).join("|"),
    keys: group.map((c) => chatCellKey(c.rowKey, c.columnKey)),
    label:
      group.length === 1
        ? `${group[0].columnLabel} · ${short(group[0].rowLabel, 32)}`
        : `${group[0].columnLabel} · ${group.length} rows`,
  }));
};

/** What the agent reads with the message: each cell's row id and column key (the ids its commands take), value and quote. */
export const cellContextText = (datasetId: string | undefined, cells: ChatCell[]) =>
  [
    `The message is about these ${cells.length === 1 ? "cell" : `${cells.length} cells`} of the dataset:`,
    ...cells.map(
      (c) =>
        `- ${short(c.rowLabel, 80)} (row id ${c.rowKey}), column "${c.columnLabel}" (key ${c.columnKey}): ` +
        (c.value ? `"${short(c.value, 200)}"${c.quote && c.quote !== c.value ? `, from "${short(c.quote, 240)}"` : ""}` : "empty"),
    ),
    ...(datasetId ? [`paperclip dataset cell ${datasetId} <row id> <key> shows a cell's evidence.`] : []),
  ].join("\n");
